import { Badge, Box, Button, Card, Collapse, Flex, Modal, Stack, Text, TextInput } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronUp,
  IconDoorEnter,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import Loader from 'components/Loader';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Medium from 'theme/components/Typography/P1Medium';
import P2Medium from 'theme/components/Typography/P2Medium';
import P2Regular from 'theme/components/Typography/P2Regular';

export interface TemporaryPinCodeViewModel {
  id: number;
  pinCode: string;
  title: string | null;
  validFrom: string;
  validTo: string;
  syncFrom: string;
  syncUntil: string;
  canceledAt: string | null;
}

export interface TemporaryPinCodePresenterProps {
  backPath: string;
  backLabel: string;
  loading: boolean;
  creatingTemporaryPin: boolean;
  cancelingTemporaryPinId: number | null;
  temporaryPinCodes: TemporaryPinCodeViewModel[];
  temporaryPinModalOpened: boolean;
  temporaryPinTitle: string;
  temporaryPinValidFrom: Date | null;
  temporaryPinValidTo: Date | null;
  initialArchivedTemporaryPinsOpened?: boolean;
  initialTemporaryPinPendingCancelId?: number | null;
  onOpenTemporaryPinModal: () => void;
  onCloseTemporaryPinModal: () => void;
  onTemporaryPinTitleChange: (value: string) => void;
  onTemporaryPinValidFromChange: (value: Date | null) => void;
  onTemporaryPinValidToChange: (value: Date | null) => void;
  onCreateTemporaryPin: () => void;
  onCancelTemporaryPin: (temporaryPinCodeId: number) => void;
  _t: (msgid: string) => string;
}

/**
 * Formats a Date value for native datetime-local inputs.
 */
function formatDateTimeLocalValue(value: Date | null) {
  if (!value) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parses a native datetime-local input value into a Date.
 */
function parseDateTimeLocalValue(value: string) {
  if (!value) {
    return null;
  }

  const [datePart, timePart = ''] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return new Date(year, month - 1, day, hours, minutes);
}

/**
 * Returns a formatted timestamp for display.
 */
function formatDateTime(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

/**
 * Derives the current status badge of a temporary PIN.
 */
function getTemporaryPinStatus(
  temporaryPinCode: TemporaryPinCodeViewModel,
  now: Date,
  _t: (msgid: string) => string
): { label: string; color: string } {
  if (temporaryPinCode.canceledAt) {
    return { label: _t('Canceled'), color: '#D92D20' };
  }

  const validFrom = new Date(temporaryPinCode.validFrom);
  const validTo = new Date(temporaryPinCode.validTo);

  if (now < validFrom) {
    return { label: _t('Scheduled'), color: '#175CD3' };
  }

  if (now <= validTo) {
    return { label: _t('Active'), color: '#1B8F34' };
  }

  return { label: _t('Finishing sync'), color: '#B54708' };
}

/**
 * Returns whether a temporary PIN is already canceled or no longer valid for entry use.
 */
function isArchivedTemporaryPin(temporaryPinCode: TemporaryPinCodeViewModel, now: Date) {
  if (temporaryPinCode.canceledAt) {
    return true;
  }

  return now > new Date(temporaryPinCode.validTo);
}

/**
 * Renders the temporary PIN page UI independently of the backing data source.
 */
export default function TemporaryPinCodePresenter({
  backPath,
  backLabel,
  loading,
  creatingTemporaryPin,
  cancelingTemporaryPinId,
  temporaryPinCodes,
  temporaryPinModalOpened,
  temporaryPinTitle,
  temporaryPinValidFrom,
  temporaryPinValidTo,
  initialArchivedTemporaryPinsOpened = false,
  initialTemporaryPinPendingCancelId = null,
  onOpenTemporaryPinModal,
  onCloseTemporaryPinModal,
  onTemporaryPinTitleChange,
  onTemporaryPinValidFromChange,
  onTemporaryPinValidToChange,
  onCreateTemporaryPin,
  onCancelTemporaryPin,
  _t,
}: TemporaryPinCodePresenterProps) {
  const now = new Date();
  const locale = 'sk-SK';
  const [archivedTemporaryPinsOpened, setArchivedTemporaryPinsOpened] = useState(initialArchivedTemporaryPinsOpened);
  const [temporaryPinPendingCancelId, setTemporaryPinPendingCancelId] = useState<number | null>(
    initialTemporaryPinPendingCancelId
  );

  const activeTemporaryPinCodes = temporaryPinCodes.filter(
    (temporaryPinCode) => !isArchivedTemporaryPin(temporaryPinCode, now)
  );
  const archivedTemporaryPinCodes = temporaryPinCodes.filter((temporaryPinCode) =>
    isArchivedTemporaryPin(temporaryPinCode, now)
  );

  const archivedTemporaryPinsToggleLabel = archivedTemporaryPinsOpened
    ? `${_t('Hide canceled and expired PIN codes')} (${archivedTemporaryPinCodes.length})`
    : `${_t('Show canceled and expired PIN codes')} (${archivedTemporaryPinCodes.length})`;

  /**
   * Renders one temporary PIN card in either the primary or archived section.
   */
  const renderTemporaryPinCard = (temporaryPinCode: TemporaryPinCodeViewModel, archived = false) => {
    const status = getTemporaryPinStatus(temporaryPinCode, now, _t);

    return (
      <Card key={temporaryPinCode.id} radius="md" withBorder padding="sm">
        <Stack gap="0.5rem">
          <Flex justify="space-between" align="flex-start" gap="0.5rem">
            <Flex align="center" gap="0.5rem" wrap="wrap">
              <Badge color={status.color} variant="light" size="sm">
                {status.label}
              </Badge>
              <P2Medium color={theme.colors?.gray?.[7]}>{temporaryPinCode.title || _t('PIN code')}</P2Medium>
            </Flex>

            {!archived && (
              <Button
                color="red"
                variant="subtle"
                size="compact-sm"
                px={0}
                leftSection={<IconTrash size="0.95rem" />}
                loading={cancelingTemporaryPinId === temporaryPinCode.id}
                onClick={() => setTemporaryPinPendingCancelId(temporaryPinCode.id)}
              >
                {_t('Cancel')}
              </Button>
            )}
          </Flex>

          <Box
            style={{
              borderRadius: '0.75rem',
              backgroundColor: theme.colors?.gray?.[1],
              padding: '0.75rem 0.875rem',
            }}
          >
            <P1Medium
              color={theme.colors?.gray?.[12]}
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                letterSpacing: '0.16em',
                lineHeight: 1,
              }}
            >
              {temporaryPinCode.pinCode}
            </P1Medium>
          </Box>

          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '0.75rem',
            }}
          >
            <Box>
              <P2Medium color={theme.colors?.gray?.[7]} mb="0.15rem">
                {_t('Valid from')}
              </P2Medium>
              <P2Regular color={theme.colors?.gray?.[12]}>
                {formatDateTime(temporaryPinCode.validFrom, locale)}
              </P2Regular>
            </Box>

            <Box>
              <P2Medium color={theme.colors?.gray?.[7]} mb="0.15rem">
                {_t('Valid to')}
              </P2Medium>
              <P2Regular color={theme.colors?.gray?.[12]}>{formatDateTime(temporaryPinCode.validTo, locale)}</P2Regular>
            </Box>
          </Box>
        </Stack>
      </Card>
    );
  };

  return (
    <Box p="1rem">
      <Box mb="1rem">
        <Flex>
          <Link
            to={backPath}
            style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
          >
            <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
            <P2Medium color={theme.colors?.gray?.[9]}>{backLabel}</P2Medium>
          </Link>
        </Flex>
        <Flex gap="0.5rem" align="center" mb="0.5rem">
          <IconDoorEnter size="2rem" color={theme.colors?.gray?.[12]} />
          <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('Temporary entries')}</H1SemiBold>
        </Flex>
      </Box>
      {loading ? (
        <Loader />
      ) : (
        <Box
          style={{
            backgroundColor: 'white',
            borderRadius: '0.625rem',
            padding: '0.875rem',
          }}
        >
          <Stack gap="0.75rem" mb="0.75rem">
            <Box pr="2rem">
              <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Temporary entries')}</H2SemiBold>
              <P2Regular color={theme.colors?.gray?.[6]}>
                {_t('Create up to 5 temporary 6-digit PIN codes with a fixed start and end time.')}
              </P2Regular>
            </Box>
            <Button leftSection={<IconPlus size="1rem" />} onClick={onOpenTemporaryPinModal} fullWidth>
              {_t('Add temporary PIN')}
            </Button>
          </Stack>

          {activeTemporaryPinCodes.length === 0 ? (
            <P2Medium color={theme.colors?.gray?.[6]}>{_t('No temporary PIN codes are scheduled right now.')}</P2Medium>
          ) : (
            <Stack gap="0.625rem">
              {activeTemporaryPinCodes.map((temporaryPinCode) => renderTemporaryPinCard(temporaryPinCode))}
            </Stack>
          )}

          {archivedTemporaryPinCodes.length > 0 && (
            <Box mt="1rem">
              <Button
                variant="subtle"
                size="compact-md"
                px={0}
                onClick={() => setArchivedTemporaryPinsOpened((opened) => !opened)}
                rightSection={
                  archivedTemporaryPinsOpened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />
                }
              >
                {archivedTemporaryPinsToggleLabel}
              </Button>

              <Collapse in={archivedTemporaryPinsOpened} mt="0.75rem">
                <Stack gap="0.625rem">
                  {archivedTemporaryPinCodes.map((temporaryPinCode) => renderTemporaryPinCard(temporaryPinCode, true))}
                </Stack>
              </Collapse>
            </Box>
          )}
        </Box>
      )}

      <Modal
        opened={temporaryPinModalOpened}
        onClose={onCloseTemporaryPinModal}
        title={_t('Create temporary PIN code')}
        centered
      >
        <Stack gap="0.75rem">
          <Text c="dimmed" size="sm">
            {_t('Choose when the temporary PIN should become valid and when it should expire.')}
          </Text>

          <TextInput
            label={_t('Title')}
            placeholder={_t('Temporary PIN title (optional)')}
            value={temporaryPinTitle}
            onChange={(event) => onTemporaryPinTitleChange(event.currentTarget.value)}
            maxLength={100}
          />

          <TextInput
            type="datetime-local"
            label={_t('Valid from')}
            value={formatDateTimeLocalValue(temporaryPinValidFrom)}
            onChange={(event) => onTemporaryPinValidFromChange(parseDateTimeLocalValue(event.currentTarget.value))}
            step={60}
          />

          <TextInput
            type="datetime-local"
            label={_t('Valid to')}
            value={formatDateTimeLocalValue(temporaryPinValidTo)}
            onChange={(event) => onTemporaryPinValidToChange(parseDateTimeLocalValue(event.currentTarget.value))}
            min={formatDateTimeLocalValue(temporaryPinValidFrom)}
            step={60}
          />

          <Button onClick={onCreateTemporaryPin} loading={creatingTemporaryPin}>
            {_t('Create temporary PIN')}
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={temporaryPinPendingCancelId !== null}
        onClose={() => setTemporaryPinPendingCancelId(null)}
        title={_t('Cancel temporary PIN code')}
        centered
      >
        <Stack gap="0.75rem">
          <Text c="dimmed" size="sm">
            {_t('Do you really want to cancel this temporary PIN code?')}
          </Text>

          <Flex gap="0.75rem">
            <Button variant="default" fullWidth onClick={() => setTemporaryPinPendingCancelId(null)}>
              {_t('Back')}
            </Button>
            <Button
              color="red"
              fullWidth
              loading={cancelingTemporaryPinId === temporaryPinPendingCancelId}
              onClick={() => {
                if (temporaryPinPendingCancelId === null) {
                  return;
                }

                const temporaryPinCodeId = temporaryPinPendingCancelId;
                setTemporaryPinPendingCancelId(null);
                onCancelTemporaryPin(temporaryPinCodeId);
              }}
            >
              {_t('Cancel')}
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </Box>
  );
}
